import {DceComponent, DceHTMLElement} from "udce-engine";
import Dce from "udce-engine/lib/decorators/Dce";
import BasicScene from "app/lib/components/three/BasicScene";
import {DATA, PeriodicElement} from "app/lib/components/PeriodicData";
import {PERIODIC_DEF_LABELS, PeriodicLabels, PeriodicLabelsDict} from "app/lib/components/PeriodicLabels";

interface PeriodicTableProps {
    lang: string,
    labels?: PeriodicLabels
}

const SVG_CLOSE = "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\">\n" +
    "<path fill=\"currentColor\" d=\"M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z\" />\n" +
    "</svg>"

function el(className: string, inner: string){
    let div = document.createElement("div");
    div.innerHTML = inner;
    div.classList.add(className);
    return div;
}

function elWithSub(className: string, ...inner: Element[]){
    let div = document.createElement("div");
    div.append(...inner);
    div.classList.add(className);
    return div;
}


class DataElement {

    private label: string;
    private div: HTMLElement;
    private updateCallback: (e: PeriodicElement, div: HTMLElement) => void;

    constructor(label: string, update: (e: PeriodicElement, div: HTMLElement) => void) {
        this.label = label;
        this.updateCallback = update;
    }

    public update(e: PeriodicElement){
        this.updateCallback(e, this.div);
    }

    public create(): HTMLElement{
        this.div = el("pdata-vk-v", '');

        return elWithSub(
            'pdata-vk',
            el('pdata-vk-k', this.label),
            this.div
        );
    }
}

@Dce("periodicTable")
export default class PeriodicTable extends DceComponent<PeriodicTableProps> {

    private lang: string;
    private labels: PeriodicLabels;

    private table: HTMLElement;
    private scene: BasicScene;
    private elements: HTMLElement[];

    private canvas: HTMLCanvasElement;
    private dataName: HTMLDivElement;
    private dataType: HTMLDivElement;
    private dataClose: HTMLDivElement;

    private dataLeft: DataElement[];
    private dataRight: DataElement[];
    private sidebarElement: HTMLDivElement;

    constructor(elem: DceHTMLElement, props: PeriodicTableProps) {
        super(elem, props);

        this.lang = props.lang

        if(!this.props.labels){
            if(PERIODIC_DEF_LABELS[this.lang]){
                this.labels = PERIODIC_DEF_LABELS[this.lang]
            }else{
                this.labels = PERIODIC_DEF_LABELS["en"]
            }
        }else{
            this.labels = this.props.labels;
        }


        this.table = document.getElementById('ptable');

        let types: string[] = [];

        DATA.forEach(v => {

            let kategoria = v.kategoria_code;

            if(types.indexOf(kategoria) == -1){
                types.push(kategoria);
            }

            let telement = this.createTableElement(
                v.symbol,v.snazwa[this.lang], v.protony, v.okres, v.grupa, kategoria
            );
            this.table.append(telement);

            telement.addEventListener('click', (e) => {
                if(telement.classList.contains('active')){
                    return;
                }

                this.elem.classList.remove('deselected');

                if(this.sidebarElement.classList.contains('closed')){
                    this.sidebarElement.classList.remove('closed');
                }

                let oldtype = '';
                this.table.querySelectorAll('.active').forEach(oa => {
                    let h = (oa as HTMLElement);
                    h.classList.remove('active');
                    oldtype = h.dataset['type'];
                });

                if(telement.dataset['type'] != oldtype){

                    this.table.querySelectorAll('.highlight').forEach(oh => {
                        (oh as HTMLElement).classList.remove('highlight');
                    });


                    this.table.querySelectorAll('.' + telement.dataset['type']).forEach(nh => {
                        (nh as HTMLElement).classList.add('highlight');
                    });
                }

                telement.classList.add('active');

                this.selectElement(v, kategoria);

            });

        })

        this.createSidebar();

        let canvasrect = this.canvas.getBoundingClientRect();

        let width = canvasrect.width;
        if(window.innerWidth < 921 && canvasrect.width > 400){
            width = 400;
        }

        this.scene = new BasicScene(this.canvas);
        this.scene.initialize(true, false, width, width);
        this.scene.setElement(4, 5, 1, [8]);
        this.loop();

    }



    private selectElement(elem: PeriodicElement, kategoriaSlug: string){
        this.dataName.innerHTML = elem.snazwa[this.lang] + ' (' + elem.symbol + ')<sup>' + elem.protony + '</sup>';
        this.dataType.innerHTML = elem.kategoria[this.lang];

        this.dataType.className = "pdata-type";
        this.dataType.classList.add(kategoriaSlug);

        let p = elem.rozklad_powlok.split(',').map(a => {
            return parseInt(a);
        }).reverse();

        this.dataLeft.forEach(de => {
            de.update(elem);
        });

        this.dataRight.forEach(de => {
            de.update(elem);
        })

        this.scene.setElement(elem.protony, elem.neutrony, p.length, p)
    }

    private createSidebar(){

        let div = document.createElement('div');
        div.classList.add('pdata');
        div.classList.add('closed');

        this.sidebarElement = div;

        let divCont = el('pdata-cont', '');
        div.append(divCont);

        let canvasWrap = el('canvas', '');
        this.canvas = document.createElement('canvas');
        this.canvas.style.width = "100%";
        this.canvas.style.height = "100%";

        canvasWrap.append(this.canvas);

        this.dataName = el('pdata-name', '');
        this.dataType = el('pdata-type', '');
        this.dataClose = el('pdata-close', '');

        this.dataClose.addEventListener('click', (e) => {
            this.sidebarElement.classList.add('closed');
            this.elem.classList.add('deselected');
        })

        this.dataClose.innerHTML = SVG_CLOSE;

        this.dataLeft = [
            new DataElement(this.labels.postac, (e,d) => {
                d.innerHTML = e.postac[this.lang];
            }),
            new DataElement(this.labels.wyst_w_przyrodzie, (e,d) => {
                d.innerHTML = e.wyst_w_przyrodzie[this.lang];
            }),
            new DataElement(this.labels.odkryty_przez, (e,d) => {
                d.innerHTML = e.odkryty_przez;
            }),
            new DataElement(this.labels.rok_odkrycia, (e,d) => {
                d.innerHTML = e.rok_odkrycia;
            }),
        ];

        this.dataRight = [
            new DataElement(this.labels.masa_atomowa, (e,d) => {
                d.innerHTML = e.masa_atomowa;
            }),
            new DataElement(this.labels.gestosc, (e,d) => {
                if(this.lang == 'pl'){
                    if(e.gestoscPL){
                        d.innerHTML = e.gestoscPL;
                        return;
                    }
                }
                d.innerHTML = e.gestosc;
            }),
            new DataElement(this.labels.temp_topnienia, (e,d) => {
                if(this.lang == 'pl') {
                    if (e.temp_topnieniaPL) {
                        d.innerHTML = e.temp_topnieniaPL;
                        return;
                    }
                }
                d.innerHTML = e.temp_topnienia;
            }),
            new DataElement(this.labels.temp_wrzenia, (e,d) => {
                d.innerHTML = e.temp_wrzenia;
            }),
        ];

        divCont.append(
            canvasWrap,

            elWithSub(
                'pdata-head',
                this.dataClose,
                this.dataName,
                this.dataType,
            ),

            elWithSub(
                'pdata-values',
                elWithSub(
                    'pdata-values-left',
                    ...(this.dataLeft.map(a => a.create()))
                ),
                elWithSub(
                    'pdata-values-right',
                    ...(this.dataRight.map(a => a.create()))
                )
            )
        );

        document.body.append(div);

        return div;

    }



    private createTableElement(symbol: string, nazwa: string, protony: number, okres: number, grupa: number, type: string): HTMLElement {

        let div = document.createElement('div');
        div.classList.add('e');
        div.classList.add(type);

        div.dataset['type'] = type;

        div.append(
            el("num", protony + ""),
            el("c", symbol + ""),
            el("name", nazwa + ""),
        )

        let x = grupa;
        let y = okres;

        if(grupa == 3 && okres == 6 && protony > 57){
            //lantanowce

            y = 9;
            x = 4 + protony - 57;

        }else if(grupa == 3 && okres == 7 && protony > 89){
            //aktynowce

            y = 10;
            x = 4 + protony - 89;

        }else {
            if(grupa > 3) {
                x = grupa + 1;
            }

        }

        div.style.gridColumn = x + '';
        div.style.gridRow = y + '';

        return div;
    }


    private loop() {

        this.scene.camera.updateProjectionMatrix();
        this.scene.animate();
        this.scene.renderer.render(this.scene, this.scene.camera);
        this.scene.orbitals.update();

        requestAnimationFrame(() => this.loop());
    }

}